import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, ArrowRight, ChevronRight, HelpCircle, CheckCircle2, Home } from 'lucide-react';
import SEO, { buildFAQSchema, buildArticleSchema, buildBreadcrumbSchema } from '../components/SEO';
import { KNOWLEDGE_ARTICLES, SITE_URL, BRAND } from '../lib/seoContent';

/**
 * Generic renderer for all Knowledge / Q&A articles listed in seoContent.KNOWLEDGE_ARTICLES.
 * Route is /:slug — the slug maps to an entry in KNOWLEDGE_ARTICLES.
 * Falls back to a NotFound-like message if slug not recognised.
 */
const KnowledgePage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const article = KNOWLEDGE_ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F7F8F5] flex items-center justify-center px-6" data-testid="knowledge-not-found">
        <div className="text-center max-w-md">
          <BookOpen className="h-12 w-12 text-[#004534] mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#004534] mb-3">Article not found</h1>
          <Link to="/" className="text-[#D4AF37] font-semibold hover:underline">Return home →</Link>
        </div>
      </div>
    );
  }

  const url = `${SITE_URL}/${article.slug}`;
  const jsonLd = [
    buildArticleSchema({
      headline: article.heroTitle,
      description: article.metaDescription,
      url,
      datePublished: article.published,
      dateModified: article.modified,
    }),
    buildFAQSchema(article.faqs || []),
    buildBreadcrumbSchema([
      { name: 'Home', url: `${SITE_URL}/` },
      { name: 'Knowledge', url: `${SITE_URL}/` },
      { name: article.heroTitle, url },
    ]),
  ];

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid={`knowledge-page-${slug}`}>
      <SEO
        title={article.title}
        description={article.metaDescription}
        canonical={url}
        keywords={article.keywords}
        jsonLd={jsonLd}
        ogType="article"
        articlePublished={article.published}
        articleModified={article.modified}
      />

      {/* Hero */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/40 to-transparent" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-4xl">
          {/* Breadcrumb */}
          <nav className="mb-6 text-xs text-white/50 flex items-center gap-2" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D4AF37] flex items-center gap-1"><Home className="h-3 w-3" /> Home</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-white/80 truncate">{article.heroTitle}</span>
          </nav>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-xs font-bold uppercase tracking-wider mb-6">
              <BookOpen className="h-3.5 w-3.5" /> {article.heroKicker}
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 font-['Outfit'] leading-tight">
              {article.heroTitle}
            </h1>
            <p className="text-lg text-[#D4AF37] font-medium mb-4">{article.heroSubtitle}</p>
            <p className="text-white/70 max-w-3xl leading-relaxed">{article.heroSummary}</p>
          </motion.div>
        </div>
      </section>

      {/* Sections */}
      <section className="py-14">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="space-y-10">
            {article.sections.map((sec, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
              >
                <h2 className="text-2xl md:text-3xl font-bold text-[#004534] mb-3 font-['Outfit'] flex items-start gap-3">
                  <span className="inline-block w-1 h-7 bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                  {sec.heading}
                </h2>
                <p className="text-gray-700 leading-relaxed text-base whitespace-pre-line">{sec.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      {article.faqs && article.faqs.length > 0 && (
        <section className="py-14 bg-white border-y" data-testid="knowledge-faqs">
          <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
            <div className="flex items-center gap-3 mb-8">
              <HelpCircle className="h-6 w-6 text-[#D4AF37]" />
              <h2 className="text-3xl font-bold text-[#004534] font-['Outfit']">Frequently Asked Questions</h2>
            </div>
            <div className="space-y-4">
              {article.faqs.map((f, i) => (
                <details key={i} className="group bg-[#F7F8F5] rounded-xl border border-gray-200 p-5 hover:border-[#D4AF37]/40 transition-colors" data-testid={`faq-${i}`}>
                  <summary className="cursor-pointer flex items-start justify-between gap-4 font-semibold text-[#004534] text-base list-none">
                    <span>{f.q}</span>
                    <ChevronRight className="h-5 w-5 flex-shrink-0 mt-0.5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <p className="mt-3 text-gray-700 leading-relaxed text-sm">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Author / E-E-A-T card */}
      <section className="py-10 bg-[#F7F8F5]">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 flex items-start gap-5">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#004534] to-[#0A0D0C] flex items-center justify-center text-white font-bold text-xl flex-shrink-0">
              C
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Reviewed & Published by</p>
              <p className="font-bold text-[#004534]">{BRAND}</p>
              <p className="text-sm text-gray-600 mt-1">Manufacturer of Pre-Laminated 6H Acrylic Boards with CriLam PUR-Bond™ Technology · Kanpur, Uttar Pradesh · Est. India&apos;s largest PUR-bonded lamination facility in the region.</p>
              <div className="mt-3 flex flex-wrap gap-4 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Published {article.published}</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Fact-checked</span>
                <span className="inline-flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> 10-year field data</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-gradient-to-br from-[#004534] to-[#0A0D0C] text-white">
        <div className="container mx-auto px-6 lg:px-12 max-w-3xl text-center">
          <h3 className="text-3xl md:text-4xl font-bold mb-4 font-['Outfit']">{article.cta?.title || 'Ready to Design Yours?'}</h3>
          <p className="text-white/70 mb-8">Bring CriLam&apos;s PUR-Bonded 6H Acrylic Boards into your home.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to={article.cta?.link || '/ai-visualizer'} data-testid="knowledge-cta-primary">
              <button className="px-8 py-4 bg-[#D4AF37] text-[#004534] font-bold rounded-lg hover:bg-white transition-colors flex items-center gap-2">
                {article.cta?.button || 'Explore Design Studio'} <ArrowRight className="h-5 w-5" />
              </button>
            </Link>
            <Link to="/contact" data-testid="knowledge-cta-contact">
              <button className="px-8 py-4 border-2 border-white/40 text-white font-medium rounded-lg hover:bg-white/10">
                Talk to CriLam <ChevronRight className="h-5 w-5 inline" />
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default KnowledgePage;
